import * as React from "react";

import GoogleMapReact from "google-map-react";
import { fitBounds } from "google-map-react/utils";
import { Slug } from "@fnv-core/utils";
import { FormControlProps } from "../interfaces/FormControlProps";
import { FormFieldStateHelpers } from "./helpers/FormFieldStateHelpers";
import { FormStylingHelpers } from "./helpers/FormStylingHelpers";
import { FormLabelHelpers } from "./helpers/FormLabelHelpers";
import { PropertyHelpers } from "../helpers/PropertyHelpers";
import { FormControlHelpers } from "./helpers/FormControlHelpers";
import fetch from "cross-fetch";
import { StringHelpers } from "../helpers/StringHelpers";
import { LabelAndPopover } from "./components/label-and-popover";
import { InputState } from "./Input";
import { AppointmentPlannerService } from "../services/appointments/AppointmentPlannerService";
import {
  AppointmentType,
  RegionalOfficeModel,
} from "reactcomponents/services/appointments/interfaces";
import _ from "lodash";

export interface ChooseLocationInputState extends InputState {
  dataSourceCounter: number;
  timer: any;
  showInfowindow: string;
  regionalOffices: RegionalOfficeModel[];
  appointmentType?: AppointmentType;
  center: any;
  zoom: number;
  hideUI: boolean;
  isLoading: boolean;
  locationMethod: string;
}

const InfoWindow = ({
  show,
  regionalOfficeId,
  regionalOfficeName,
  street,
  zipcode,
  onSelect,
}) => {
  const onClick = (event) => {
    event.preventDefault();
    onSelect(regionalOfficeId);
    return false;
  };

  return show ? (
    <div className="google-maps__info-window">
      <strong>{regionalOfficeName}</strong>
      <br />
      {street}
      <br />
      {zipcode}
      <br />
      <strong>
        <a href="#" onClick={onClick}>
          Kies deze locatie{" "}
          <span className="icon icon-open-chevron-right"></span>
        </a>
      </strong>
    </div>
  ) : null;
};

const MarkerComponent = ({
  lat,
  lng,
  text,
  show,
  regionalOfficeId,
  regionalOfficeName,
  street,
  zipcode,
  onSelect,
}) => (
  <div className="choose-location__marker">
    <img width="32" src="/assets/img/layout/map-marker.svg" alt={text}></img>
    <InfoWindow
      show={show}
      regionalOfficeId={regionalOfficeId}
      regionalOfficeName={regionalOfficeName}
      street={street}
      zipcode={zipcode}
      onSelect={onSelect}
    />
  </div>
);

class ChooseLocation extends React.Component<
  FormControlProps,
  ChooseLocationInputState
> {
  unmounted: boolean = true;

  constructor(props: FormControlProps) {
    super(props);
    this.state = {
      dataSourceCounter: 0,
      timer: undefined,
      showInfowindow: "",
      regionalOffices: [],
      appointmentType: undefined,
      isLoading: false,
      hideUI: true,
      center: {
        lat: this.props.formFieldDefinition.latitudeMapCenter || 52.1028157,
        lng: this.props.formFieldDefinition.longitudeMapCenter || 5.0620636,
      },
      zoom: 11,
      hasFocus: false,
      isButtonClicked: false,
      isErrorLoading: false,
      locationMethod: this.props.formikBag.values[
        this.props.formFieldDefinition.LocationMethodField
      ]
    };
    this.googleMap = React.createRef();
  }

  googleMap: any;

  onMarkerClick = (key, childProps) => {
    this.setState({
      showInfowindow: childProps.regionalOfficeId,
      center: {
        lat: childProps.lat,
        lng: childProps.lng,
      },
    });
  }

  onSelectLocation = function (value, event?) {
    const filterRegionalOffices: RegionalOfficeModel[] =
      this.state.regionalOffices.filter(
        (regionalOffice: RegionalOfficeModel, index) => {
          return regionalOffice.documentGuid == value;
        }
      );

    this.props.formController.setPayload({});
    if (filterRegionalOffices.length > 0) {
      this.props.formController.setPayload(filterRegionalOffices[0]);
    }
    this.setState({ showInfowindow: "" }, () => {
      FormFieldStateHelpers.clearMarkedFields(this);

      setTimeout(() => {
        FormFieldStateHelpers.updateFormikField(this, value);

        if (this.props.formFieldDefinition.isDependentField) {
          this.props.formController.setState({
            [this.props.formFieldDefinition.name]: value,
          });
        }
      }, 10);
    });
  };

  onSelectLocationViaInfoWindow = (value) => {
    this.onSelectLocation(value);
  }

  googleMapsLoaded: boolean = false;
  step1Submitted: boolean = false;
  componentDidMount() {
    this.unmounted = false;

    let payload = this.props.formController.getPayload();
    let forceGetAllLocations = false;
    if (
      payload &&
      payload["HasRegionalOfficeLocation"] !== undefined &&
      !payload["HasRegionalOfficeLocation"]
    ) {
      forceGetAllLocations = true;
    }

    if (!this.unmounted) {
      if (!forceGetAllLocations) {
        this.setState({ hideUI: false });
      } else if (
        !!this.props.formFieldDefinition.hideGoogleMaps ||
        !!forceGetAllLocations ||
        this.googleMapsLoaded ||
        (window as any).google
      ) {
        this.getLocation(
          this.state.locationMethod
        );
      }
      //load chosen appointmenttype object
      const appointmentTypeId = this.props.formikBag.values
        ?.afspraaktype as number;

      if (appointmentTypeId) {
        AppointmentPlannerService.getAppointmentTypeById(
          appointmentTypeId
        ).then((x) => {
          if (x && !this.unmounted) {
            // dirty fix. Is considered an antipattern in react. Needed because it is an async function call
            this.setState({ appointmentType: x });

            // change subtitle
            const el = document.getElementsByClassName("form__step-subtitle");
            if (el !== undefined && el.length > 0) {
              el[0].innerHTML = el[0].innerHTML.replace(
                "{AppointmentTypePlaceholder}",
                this.state.appointmentType.description
              );
            }

            // check if we should skip to step 2 immediately
            if (!x.hasRegionalOfficeLocation) {
              this.props.formikBag.setFieldValue(this.props.formFieldDefinition.LocationMethodField, "kaart");
            }
          }
        });
      }
    }
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  onGoogleMapsLoaded = async () => {
    this.googleMapsLoaded = true;
    this.getLocation(
      this.state.locationMethod
    );
  }

  renderLocations = (regionalOffices: RegionalOfficeModel[]) => {
    let center: any = undefined;
    let zoom = 11;

    if (!this.props.formFieldDefinition.hideGoogleMaps) {
      let hasLatLongLocations = false;
      regionalOffices.forEach((p) => {
        if (!isNaN(p.latitude) && !isNaN(p.longitude)) {
          hasLatLongLocations = true;
        }
      });

      if (!!hasLatLongLocations) {
        let google = (window as any).google;
        if (google) {
          let bounds = new google.maps.LatLngBounds();

          regionalOffices.forEach((p) => {
            if (!isNaN(p.latitude) && !isNaN(p.longitude)) {
              bounds.extend(new google.maps.LatLng(p.latitude, p.longitude));
            }
          });

          const ne = bounds.getNorthEast();
          const sw = bounds.getSouthWest();
          const nw = { lat: ne.lat(), lng: sw.lng() };
          const se = { lat: sw.lat(), lng: ne.lng() };

          let width = 780;

          if (this.googleMap && this.googleMap.current) {
            width = this.googleMap.current.clientWidth;
          }

          let calcBounds = fitBounds(
            {
              se: { lat: se.lat, lng: se.lng },
              nw: { lat: nw.lat, lng: nw.lng },
            },
            { width: width, height: 300 }
          );
          center = calcBounds.center;
          zoom = calcBounds.zoom;
        }
      }
    }

    let autoSelect = false;
    if (
      regionalOffices.length == 1 &&
      !isNaN(regionalOffices[0].latitude) &&
      !isNaN(regionalOffices[0].longitude) &&
      regionalOffices[0].latitude !== 0 &&
      regionalOffices[0].longitude !== 0
    ) {
      center = {
        lat: regionalOffices[0].latitude,
        lng: regionalOffices[0].longitude,
      };
      zoom = 11;
    } else if (
      regionalOffices.length == 1 &&
      (isNaN(regionalOffices[0].latitude) ||
        isNaN(regionalOffices[0].longitude) ||
        regionalOffices[0].latitude == 0 ||
        regionalOffices[0].longitude == 0)
    ) {
      autoSelect = true;
    }

    this.setState(
      {
        regionalOffices: regionalOffices,
        center: center,
        zoom: zoom,
        hideUI: autoSelect,
      },
      () => {
        if (regionalOffices.length == 1 && !!autoSelect) {
          this.props.formController.setPayload(regionalOffices[0]);
          FormFieldStateHelpers.updateFormikField(
            this,
            regionalOffices[0].documentGuid
          );
        } else if (
          this.props.field.value !== undefined &&
          this.props.field.value != ""
        ) {
          let isRegionalOfficeFound = false;
          let regionalOfficeDocumentID = this.props.field.value;
          regionalOffices.map((regionalOffice) => {
            if (regionalOffice.documentGuid == regionalOfficeDocumentID) {
              isRegionalOfficeFound = true;
            }
          });
          if (!isRegionalOfficeFound) {
            FormFieldStateHelpers.updateFormikField(this, "");
          }
        }
      }
    );
  }

  fetchLatLongByZipcode = async () => {
    let address = "";

    let zipcode =
      this.props.formikBag.values[this.props.formFieldDefinition.zipcodeField];
    //zipcode is enough for geocode to get basic location, therefore houseNumber not required
    if (zipcode !== undefined && zipcode !== "") {
      address = zipcode;
    }
    if (address === "") {
      return;
    }

    const locations = await AppointmentPlannerService.getLocations({
      appointmentTypeId: this.state.appointmentType?.appointmentId,
      address,
    });

    if (locations) {
      this.renderLocations(locations);
    }
  }

  getLocation = async (locationMethod) => {
    let payload = this.props.formController.getPayload();
    let forceGetAllLocations = false;
    if (!payload["hasRegionalOfficeLocation"]) {
      forceGetAllLocations = true;
    }
    let locations: RegionalOfficeModel[] = [];

    if (locationMethod == "postcode") {
      this.fetchLatLongByZipcode();
    } else if (locationMethod == "locatie") {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(async (position) => {
          await AppointmentPlannerService.getLocations({
            appointmentTypeId: this.state.appointmentType.appointmentId,
            latitide: position.coords.latitude,
            longitude: position.coords.longitude,
          }).then((res) => {
            locations = res;
            this.renderLocations(locations);
          });
        });
      }
    } else if (locationMethod == "kaart" || !!forceGetAllLocations) {
      locations = await AppointmentPlannerService.getLocations({
        appointmentTypeId: this.state.appointmentType.appointmentId,
      });
    }

    this.renderLocations(locations);
  }

  render() {
    const locationMethod = this.state.locationMethod;

    if (!!this.state.hideUI) {
      return <></>;
    }

    if (!!this.props.formFieldDefinition.hideGoogleMaps) {
      return <></>;
    }

    if (
      this.state.regionalOffices.length == 1 &&
      (isNaN(this.state.regionalOffices[0].latitude) ||
        isNaN(this.state.regionalOffices[0].longitude) ||
        this.state.regionalOffices[0].latitude == 0 ||
        this.state.regionalOffices[0].longitude == 0)
    ) {
      return <></>;
    }

    return (
      <>
        <div
          className={
            FormStylingHelpers.getFormGroupStyle(this.props) +
            " form__section-input " +
            Slug.fromString(this.props.formFieldDefinition.name) +
            "-input-form-group"
          }
        >
          <LabelAndPopover
            fieldId={PropertyHelpers.getFieldID(this.props)}
            label={FormLabelHelpers.getFieldLabel(this)}
            explanation={this.props.formFieldDefinition.explanation}
            hasPopover={this.props.formFieldDefinition.hasPopover}
          ></LabelAndPopover>
          <div
            style={{
              visibility: this.state.appointmentType?.hasRegionalOfficeLocation
                ? "visible"
                : "hidden",
            }}
          >
            {this.state.regionalOffices.map((regionalOffice, index) => {

              return (
                <React.Fragment key={index}>
                  {(locationMethod !== "kaart" ||
                    (locationMethod === "kaart" &&
                      this.props.field.value ==
                        regionalOffice.documentGuid)) && (
                    <div
                      key={index}
                      tabIndex={0}
                      onClick={this.onSelectLocation.bind(
                        this,
                        regionalOffice.documentGuid
                      )}
                      data-value={regionalOffice.documentGuid}
                      className={
                        "choose-location__location " +
                        (this.props.field.value == regionalOffice.documentGuid
                          ? "choose-location__location--selected"
                          : "")
                      }
                    >
                      <img
                        className="choose-location__marker"
                        src="/assets/img/layout/map-marker.svg"
                        alt="locatie"
                      />
                      <span>
                        <strong>{regionalOffice.officeName},</strong>{" "}
                        {regionalOffice.street} {regionalOffice.streetNumber}
                        {regionalOffice.streetSuffix}
                      </span>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
          <div
            className="choose-location__map"
            style={{ height: "300px", width: "100%" }}
            ref={this.googleMap}
          >
            <GoogleMapReact
              onGoogleApiLoaded={({ map, maps }) => this.onGoogleMapsLoaded()}
              bootstrapURLKeys={{
                key: this.props.formFieldDefinition.googleMapsKey || "",
                language: "nl",
                region: "nl",
              }}
              defaultCenter={{
                lat:
                  this.props.formFieldDefinition.latitudeMapCenter ||
                  52.1028157,
                lng:
                  this.props.formFieldDefinition.longitudeMapCenter ||
                  5.0620636,
              }}
              defaultZoom={11}
              center={this.state.center}
              zoom={this.state.zoom}
              onChildClick={this.onMarkerClick}
            >
              {this.state.regionalOffices.map((regionalOffice, index) => {
                return (
                  <MarkerComponent
                    key={index}
                    lat={regionalOffice.latitude}
                    lng={regionalOffice.longitude}
                    text={regionalOffice.officeName}
                    show={
                      this.state.showInfowindow == regionalOffice.documentGuid
                    }
                    regionalOfficeId={regionalOffice.documentGuid}
                    regionalOfficeName={regionalOffice.officeName}
                    street={`${regionalOffice.street} ${regionalOffice.streetNumber}`}
                    zipcode={regionalOffice.zipcode}
                    onSelect={this.onSelectLocationViaInfoWindow}
                  />
                );
              })}
            </GoogleMapReact>
          </div>
          {FormControlHelpers.invalidFeedbackComponent(this)}
        </div>
        <div
          className={
            Slug.fromString(this.props.formFieldDefinition.name) +
            "-input-form-group-after" +
            " form-group-after "
          }
        ></div>
      </>
    );
  }
}

export { ChooseLocation };
